import type { OpencodeToolPart } from "schemas/generated/agentTools/opencode";
import type { ToolDisplayText } from "./shared";

import { reploOpencodeHarnessToolIconKeyByName } from "schemas/agentTools/reploOpencodeHarness";

import { getFilePathDisplay, getToolDisplayText } from "./shared";

type ReploOpencodeHarnessToolName =
  keyof typeof reploOpencodeHarnessToolIconKeyByName;

const REPLO_OPENCODE_HARNESS_TEXT_BY_TOOL_NAME = {
  "capture-html": getToolDisplayText({
    completed: "Read page",
    pending: "Reading page",
    running: "Reading page",
  }),
  "compile-email": getToolDisplayText({
    completed: "Compiled email",
    pending: "Compiling email",
    running: "Compiling email",
  }),
  "publish-email": getToolDisplayText({
    completed: "Published email",
    pending: "Publishing email",
    running: "Publishing email",
  }),
  "create-registry-preview": getToolDisplayText({
    completed: "Prepared registry preview",
    pending: "Preparing registry preview",
    running: "Preparing registry preview",
  }),
  "dev-control": getToolDisplayText({
    completed: "Updated preview",
    pending: "Updating preview",
    running: "Updating preview",
  }),
  "find-registry-items": getToolDisplayText({
    completed: "Searched registry",
    pending: "Searching registry",
    running: "Searching registry",
  }),
  "find-design-inspiration": getToolDisplayText({
    completed: "Searched design inspiration",
    pending: "Searching design inspiration",
    running: "Searching design inspiration",
  }),
  "download-registry-items": getToolDisplayText({
    completed: "Downloaded registry items",
    pending: "Downloading registry items",
    running: "Downloading registry items",
  }),
  "install-staged-registry-items": getToolDisplayText({
    completed: "Installed registry items",
    pending: "Installing registry items",
    running: "Installing registry items",
  }),
  "manage-tabs": getToolDisplayText({
    completed: "Managed tabs",
    pending: "Managing tabs",
    running: "Managing tabs",
  }),
  publish: getToolDisplayText({
    completed: "Published site",
    pending: "Publishing site",
    running: "Publishing site",
  }),
  "collect-site-report-evidence": getToolDisplayText({
    completed: "Read a page",
    pending: "Reading a page",
    running: "Reading a page",
  }),
  "pin-brand-imagery": getToolDisplayText({
    completed: "Saved brand imagery",
    pending: "Saving brand imagery",
    running: "Saving brand imagery",
  }),
  "scrape-branding": getToolDisplayText({
    completed: "Created brand context",
    pending: "Creating brand context",
    running: "Creating brand context",
  }),
  "set-project-context": getToolDisplayText({
    completed: "Saved brand context",
    pending: "Saving brand context",
    running: "Saving brand context",
  }),
  "suggest-followups": getToolDisplayText({
    completed: "Suggested next steps",
    pending: "Suggesting next steps",
    running: "Suggesting next steps",
  }),
  "upload-registry-items": getToolDisplayText({
    completed: "Uploaded registry items",
    pending: "Uploading registry items",
    running: "Uploading registry items",
  }),
  "upload-asset": getToolDisplayText({
    completed: "Uploaded file",
    pending: "Uploading file",
    running: "Uploading file",
  }),
  "upload-assets": getToolDisplayText({
    completed: "Uploaded files",
    pending: "Uploading files",
    running: "Uploading files",
  }),
  "validate-insights-report": getToolDisplayText({
    completed: "Validated report",
    pending: "Validating report",
    running: "Validating report",
  }),
} satisfies Record<ReploOpencodeHarnessToolName, ToolDisplayText>;

const REPLO_OPENCODE_HARNESS_FILE_PATH_TOOL_NAMES: ReadonlySet<ReploOpencodeHarnessToolName> =
  new Set(["upload-asset"]);

export function getReploOpencodeHarnessToolDisplay({
  toolName,
  toolPart,
}: {
  toolName: ReploOpencodeHarnessToolName;
  toolPart: OpencodeToolPart;
}) {
  const summary = REPLO_OPENCODE_HARNESS_TEXT_BY_TOOL_NAME[toolName];
  return REPLO_OPENCODE_HARNESS_FILE_PATH_TOOL_NAMES.has(toolName)
    ? getFilePathDisplay({ prefix: summary, toolPart })
    : { summary, detail: null };
}
